// components/TabsController.tsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { Tab, Task, AppState } from '../types';
import { TabsView } from './TabsView';
import { TaskList } from './TaskList';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'timeTracker';

const initialState: AppState = {
  tabs: [
    {
      id: uuidv4(),
      name: 'Вкладка 1',
      tasks: [],
    },
  ],
  activeTabId: '',
};

export const TabsController: React.FC = () => {
  const [state, setState] = useLocalStorage(STORAGE_KEY, initialState);
  const [newTabDialogOpen, setNewTabDialogOpen] = useState(false);
  const [newTabName, setNewTabName] = useState('');
  const [closeTabError, setCloseTabError] = useState<string | null>(null);

  // Устанавливаем активную вкладку при первой загрузке
  React.useEffect(() => {
    if (!state.activeTabId && state.tabs.length > 0) {
      setState((prev) => ({ ...prev, activeTabId: prev.tabs[0].id }));
    }
  }, [state.activeTabId, state.tabs, setState]);

  const activeTab = state.tabs.find((tab) => tab.id === state.activeTabId);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setState((prev) => ({ ...prev, activeTabId: tabId }));
    },
    [setState],
  );

  const handleAddTab = useCallback(() => {
    if (!newTabName.trim()) return;

    const newTab: Tab = {
      id: uuidv4(),
      name: newTabName.trim(),
      tasks: [],
    };

    setState((prev) => ({
      tabs: [...prev.tabs, newTab],
      activeTabId: newTab.id,
    }));

    setNewTabName('');
    setNewTabDialogOpen(false);
  }, [newTabName, setState]);

  const handleCloseTab = useCallback(() => {
    if (state.tabs.length <= 1) {
      setCloseTabError('Нельзя закрыть последнюю вкладку');
      setTimeout(() => setCloseTabError(null), 3000);
      return;
    }

    const currentIndex = state.tabs.findIndex((tab) => tab.id === state.activeTabId);
    const newTabs = state.tabs.filter((tab) => tab.id !== state.activeTabId);

    // Определяем новую активную вкладку
    let newActiveTabId: string;
    if (currentIndex === 0) {
      newActiveTabId = newTabs[0].id;
    } else {
      newActiveTabId = newTabs[currentIndex - 1].id;
    }

    setState({
      tabs: newTabs,
      activeTabId: newActiveTabId,
    });
  }, [state.tabs, state.activeTabId, setState]);

  const handleAddTask = useCallback(
    (taskData: Omit<Task, 'id'>) => {
      const newTask: Task = {
        ...taskData,
        id: uuidv4(),
      };

      setState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId ? { ...tab, tasks: [...tab.tasks, newTask] } : tab,
        ),
      }));
    },
    [setState],
  );

  const handleUpdateTask = useCallback(
    (taskId: string, updates: Partial<Task>) => {
      setState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId
            ? {
                ...tab,
                tasks: tab.tasks.map((task) =>
                  task.id === taskId ? { ...task, ...updates } : task,
                ),
              }
            : tab,
        ),
      }));
    },
    [setState],
  );

  const handleDeleteTask = useCallback(
    (taskId: string) => {
      setState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId
            ? {
                ...tab,
                tasks: tab.tasks.filter((task) => task.id !== taskId),
              }
            : tab,
        ),
      }));
    },
    [setState],
  );

  // Обновленный метод для избирательного сброса времени задач
  const handleResetTaskTimes = useCallback(
    (taskIds: string[]) => {
      setState((prev) => ({
        ...prev,
        tabs: prev.tabs.map((tab) =>
          tab.id === prev.activeTabId
            ? {
                ...tab,
                tasks: tab.tasks.map((task) =>
                  taskIds.includes(task.id)
                    ? {
                        ...task,
                        timeSpent: 0,
                        comment: '',
                        isRunning: false,
                      }
                    : task,
                ),
              }
            : tab,
        ),
      }));
    },
    [setState],
  );

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setNewTabDialogOpen(true)}
        >
          Новая вкладка
        </Button>
        <Button
          variant="outlined"
          startIcon={<Close />}
          onClick={handleCloseTab}
          color="error"
          disabled={state.tabs.length <= 1}
        >
          Закрыть вкладку
        </Button>
      </Box>

      {closeTabError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {closeTabError}
        </Alert>
      )}

      <TabsView
        tabs={state.tabs}
        activeTabId={state.activeTabId}
        onTabChange={handleTabChange}
      />

      <Box sx={{ mt: 3 }}>
        {activeTab && (
          <TaskList
            tasks={activeTab.tasks}
            onAddTask={handleAddTask}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            onResetTaskTimes={handleResetTaskTimes}
          />
        )}
      </Box>

      <Dialog
        open={newTabDialogOpen}
        onClose={() => setNewTabDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Создать новую вкладку</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название вкладки"
            value={newTabName}
            onChange={(e) => setNewTabName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTab()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTabDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={handleAddTab}
            variant="contained"
            disabled={!newTabName.trim()}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
